import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, ArrowLeft, Send, CheckCircle } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { passwordResetService } from '../../services/passwordResetService';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { isDark } = useTheme();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await passwordResetService.generateResetToken(email);
      setIsSubmitted(true);
      toast.success('Şifre sıfırlama bağlantısı email adresinize gönderildi.');
    } catch (error) {
      // console.error('Şifre sıfırlama hatası:', error);
      toast.error(error.message || 'Bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className={`min-h-screen flex items-center justify-center ${
        isDark
          ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900'
          : 'bg-gradient-to-br from-blue-50 via-white to-gray-50'
      } py-12 px-4 sm:px-6 lg:px-8`}
    >
      {/* Arka plan grid deseni */}
      <div className="absolute inset-0">
        <div
          className={`absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px] ${
            isDark ? 'opacity-10' : 'opacity-20'
          }`}
        />
      </div>

      {/* Ana kart */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative w-full max-w-md"
      >
        <div
          className={`${
            isDark
              ? 'bg-white/10 backdrop-blur-xl border-white/10'
              : 'bg-white/80 backdrop-blur-xl border-gray-200'
          } p-8 rounded-3xl border shadow-2xl`}
        >
          {/* Geri dön butonu */}
          <button
            onClick={() => navigate('/signin')}
            className={`group mb-8 flex items-center text-sm font-medium ${
              isDark ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'
            } transition-colors`}
          >
            <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" />
            Giriş sayfasına dön
          </button>

          {/* Form ve E-posta gönderildikten sonraki durum */}
          {!isSubmitted ? (
            <>
              <div className="text-center mb-8">
                <h2
                  className={`text-3xl font-bold ${
                    isDark ? 'text-white' : 'text-gray-900'
                  } mb-2`}
                >
                  Şifremi Unuttum
                </h2>
                <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                  E-posta adresinizi girin, size şifre sıfırlama bağlantısı gönderelim.
                </p>
              </div>

              <motion.form
                onSubmit={handleSubmit}
                className="space-y-6"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                {/* E-posta alanı */}
                <div>
                  <label
                    className={`block text-sm font-medium ${
                      isDark ? 'text-gray-200' : 'text-gray-700'
                    } mb-2`}
                  >
                    E-posta Adresi
                  </label>
                  <div className="relative">
                    <Mail
                      className={`absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 ${
                        isDark ? 'text-gray-400' : 'text-gray-500'
                      }`}
                    />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className={`w-full pl-12 pr-4 py-3 ${
                        isDark
                          ? 'bg-white/5 border-white/10 text-white placeholder-gray-400'
                          : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
                      } border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200`}
                      placeholder="ornek@sirket.com"
                      required
                    />
                  </div>
                </div>

                {/* Gönder butonu */}
                <motion.button
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                  type="submit"
                  className="group relative w-full flex items-center justify-center py-3 px-4 border border-transparent rounded-xl text-white bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 font-medium shadow-lg transition-all duration-200"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    // Loading durumunda dönme animasyonlu bir ikon
                    <div className="flex items-center">
                      <svg
                        className="animate-spin -ml-1 mr-2 h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 
                          0 0 5.373 0 12h4zm2 5.291A7.962 
                          7.962 0 014 12H0c0 3.042 1.135 
                          5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Gönderiliyor...
                    </div>
                  ) : (
                    <>
                      Sıfırlama Bağlantısı Gönder
                      <Send className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </>
                  )}
                </motion.button>
              </motion.form>
            </>
          ) : (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center"
            >
              <div className="mb-6 flex justify-center">
                <CheckCircle className="w-16 h-16 text-green-500" />
              </div>
              <h3
                className={`text-2xl font-bold ${
                  isDark ? 'text-white' : 'text-gray-900'
                } mb-2`}
              >
                E-posta Gönderildi
              </h3>
              <p
                className={`${
                  isDark ? 'text-gray-300' : 'text-gray-600'
                } mb-6`}
              >
                {email} adresine şifre sıfırlama bağlantısı gönderdik. Lütfen e-posta
                kutunuzu kontrol edin.
              </p>
              <div className="flex flex-col space-y-3">
                <button
                  onClick={() => navigate('/signin')}
                  className="text-blue-500 hover:text-blue-600 font-medium transition-colors"
                >
                  Giriş sayfasına dön
                </button>
                <button
                  onClick={() => window.open('https://mail.google.com/mail/u/0/#inbox', '_blank')}
                  className={`flex items-center justify-center space-x-2 py-2 px-4 rounded-lg ${
                    isDark 
                      ? 'bg-white/10 hover:bg-white/20 text-white' 
                      : 'bg-blue-50 hover:bg-blue-100 text-blue-600'
                  } transition-all duration-200`}
                >
                  <Mail className="w-4 h-4" />
                  <span>Mail'e git</span>
                </button>
              </div>
            </motion.div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default ForgotPassword;
